import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Api from 'services/api'
import styled from 'styled-components'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import { ButtonContainer, SinglePanePage, PageTitle, screenSize, PageHeader } from 'styles/shared'

import Brand from 'helpers/brand'
import Spinner from 'components/Spinner'
import blobToFileDownload from 'helpers/blobToFileDownload'

import usePaywallStatements from 'hooks/usePaywallStatements'
import Table from 'components/Table'

import { DownloadOutlined } from '@mui/icons-material'
import { color } from 'styles/colors'
import ArrowIcon from '@mui/icons-material/TrendingFlat'

type Props = {
  paywallType: string
}

const PaywallStatements = ({ paywallType = 'premium' } : Props) => {
  const { dealerId } = useParams()
  const { statements, areStatementsLoaded } = usePaywallStatements(dealerId, paywallType)
  const navigate = useNavigate()

  if (!areStatementsLoaded) {
    return <Spinner variant="page" />
  }

  const onClickStatement = (statement) => {
    let fileNamePrefix = 'diagnostics-premium'
    if (paywallType === 'hehm') {
      fileNamePrefix = 'performance-monitoring-kits'
    }
    if (!dealerId) return
    Api.Paywall.getInvoice(dealerId, statement.accountNo, statement.invoiceNo)
      .then(res => {
        if (res.content) {
          const blob = new Blob([res.content], { type: 'text/html' })
          blobToFileDownload(blob, `${fileNamePrefix} ${statement.invoiceNo}`)
        }
      })
  }

  return (
    <S.Container>
      <S.Top>
        <Button decoration="link" onPress={() => navigate(-1)} aria-label="back-button">
          <S.ArrowIcon fontSize="large" />
        </Button>

        <S.ProductLogo
          src={paywallType === 'hehm' ? Brand.hehmProductLogoPath() : Brand.premiumProductLogoPath()}
          alt={paywallType === 'hehm' ? 'HEHM system logo' : `${Brand.brandName()} logo`}
        />
        <div />
      </S.Top>
      <S.PageHeader>
        <S.PageTitle>
          {paywallType === 'hehm' ? 'Statements' : 'Premium Statements'}
        </S.PageTitle>
      </S.PageHeader>
      <S.SinglePanePage>
        <S.Form>
          <S.FixedHeader>
            <S.HeaderCell title="Invoice Date" />
            <S.HeaderCell title="Invoice Number" />
          </S.FixedHeader>
          <S.ScrollableContent>
            <Table
              items={statements}
              noDataMessage={{
                title: 'No Statements',
                description: 'Statements will show up here.'
              }}
            >
              {statements.map(statement => (
                <S.Row key={statement.invoiceNo}>
                  <S.Cell>{statement.invoiceDate}</S.Cell>
                  <S.Cell>
                    <S.DownloadIcon onClick={() => onClickStatement(statement)} />
                    <Button decoration="link" text={String(statement.invoiceNo)} onPress={() => { onClickStatement(statement) }} styles={{ text: { fontSize: 14 } }} />
                  </S.Cell>
                </S.Row>
              ))}
            </Table>
          </S.ScrollableContent>
        </S.Form>
        <S.ButtonContainer>
          <Button decoration="positive" text="Close" onPress={() => navigate(-1)} />
        </S.ButtonContainer>
      </S.SinglePanePage>
    </S.Container>
  )
}

const S = {
  Container: styled.div`
    height: 100%;
    background-color: ${color('background')};
  `,
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
     padding: 15px;
    }
  `,
  ButtonContainer: styled(ButtonContainer)`
    border-top: none;
    max-width: 800px;
    margin-top: 0px;
  `,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
      margin-top: 0;
    }
  `,
  FixedHeader: styled.div`
    position: sticky;
    top: 0;
    z-index: 10;
    background-color: ${color('rowBorder')};
    border-radius: 10px 10px 0 0;
    display: flex;
    width: 100%;
    
    @media ${screenSize.small} {
      position: sticky;
      top: 0;
    }
  `,
  ScrollableContent: styled.div`
    max-height: 580px;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    
    /* Touch devices and mobile */
    @media (hover: none) and (pointer: coarse) {
      overflow-y: auto;
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar { width: 0px; background: transparent; }
    }
    
    /* Desktop */
    @media (hover: hover) and (pointer: fine) {
      overflow-y: hidden;
      &:hover { overflow-y: auto; }
      
      &::-webkit-scrollbar {
        width: 8px;
        opacity: 0;
      }
      &:hover::-webkit-scrollbar {
        opacity: 1;
      }
      &::-webkit-scrollbar-track {
        background: transparent;
        border-radius: 4px;
      }
      &::-webkit-scrollbar-thumb {
        background-color: ${color('scrollbarThumb')};
        border-radius: 4px;
        border: 1px solid ${color('scrollbarThumbBorder')};
      }
      &:hover::-webkit-scrollbar-thumb:hover {
        background-color: ${color('hoverBackground')};
      }
      scrollbar-width: thin;
      scrollbar-color: ${color('scrollbarThumb')} transparent;
    }
  `,
  HeaderCell: styled(Table.HeaderCell)`
    background-color: ${color('rowBorder')};
    border-top-right-radius: 10px;
    border-top-left-radius: 10px;
  `,
  Cell: styled(Table.Cell)`
    width: 50%;
    max-width: 400px;
    padding-left: 20px;
    align-items: center;
  `,
  PageTitle: styled(PageTitle)`
    margin-top: 10px;
    text-transform: none;
    @media ${screenSize.small} {
      display: flex;
    }
  `,
  Form: styled.form`
    max-width: 800px;
    max-height: 640px;
    display: flex;
    flex-direction: column;
    border-radius: 10px;
    box-shadow: 0 2px 8px ${color('shadowSubtle')};
    overflow: hidden;
    background-color: ${color('paneBackground')};
  `,
  ProductLogo: styled.img`
    height: 50px;
  `,
  Top: styled.div`
    display: flex;
    align-items: center;
    padding: 20px;
    border-bottom: 1px solid ${color('border')};
    margin-bottom: 20px;
  `,
  Row: styled(Table.Row)`
    border-bottom: 1px solid ${color('rowBorder')};
    padding: 3px 0;
    align-items: center;
    background-color: ${color('titleBackground')};
    &:last-child {
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
    }

    @media ${screenSize.small} {
        flex-direction: row;
    }
  `,
  DownloadIcon: styled(DownloadOutlined)`
    color: ${color('secondaryButtonBorder')};
    cursor: pointer;
    font-size: 20px;
    margin-left: -10px;
  `,
  ArrowIcon: styled(ArrowIcon)`
    transform: rotate(180deg);
    margin-right: 10px;
    color: ${color('breadCrumbsTextHover')};
  `
}

export default PaywallStatements
